/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  Alert,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getPolizasDeCargaExterna,
  postPolizaCargaExterna,
  patchPolizaCargaExterna,
  deletePolizaCargaExterna,
  getPolizaCargaExterna,
} from 'api/nominas/polizasDeCargaExterna';

const GRUPO_OPCIONES = [
  { id: 'plantilla', descripcion: 'Plantilla' },
  { id: 'seguridad', descripcion: 'Seguridad' },
  { id: 'eventual', descripcion: 'Eventual' },
];

const PolizaCargaExternaTabla = ({
  configuracionDePolizaCargaExternaId,
  disabled = false,
  permission,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const formInstances = [form];

  const fetchData = async () => {
    if (!configuracionDePolizaCargaExternaId) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const _data = await getPolizasDeCargaExterna({
        configuracion_de_poliza_carga_externa: configuracionDePolizaCargaExternaId,
      });
      setData(formatReceived(_data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Póliza Carga Externa] error al cargar las pólizas de la configuración', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAllData = async () => {
      if (!configuracionDePolizaCargaExternaId) {
        setData([]);
        return;
      }
      setLoading(true);
      try {
        const _data = await getPolizasDeCargaExterna({
          configuracion_de_poliza_carga_externa: configuracionDePolizaCargaExternaId,
        });
        if (mounted) {
          setData(formatReceived(_data));
          setSelectedRowKeys([]);
        }
      } catch (error) {
        message.error('Error al cargar las pólizas.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAllData();
    return () => { mounted = false; };
  }, [configuracionDePolizaCargaExternaId]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);

    if (match) {
      const _item = await getPolizaCargaExterna(match.id);
      setTimeout(() => {
        if (_item) {
          form.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(_item));
            setVisible(true);
          });
        }
      });
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      values.configuracion_de_poliza_carga_externa = configuracionDePolizaCargaExternaId;

      const [key] = selectedRowKeys;

      const saved = await (key
        ? patchPolizaCargaExterna(key, values)
        : postPolizaCargaExterna(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePolizaCargaExterna(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    if (!configuracionDePolizaCargaExternaId) {
      message.warning('Debe guardar la Configuración de Póliza de Carga Externa antes de agregar renglones.');
      return;
    }
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Cuenta',
      dataIndex: 'cuenta',
      key: 'cuenta',
      width: 100,
    },
    {
      titleText: 'SCTA',
      dataIndex: 'scta',
      key: 'scta',
      width: 100,
    },
    {
      titleText: 'SSCTA',
      dataIndex: 'sscta',
      key: 'sscta',
      width: 100,
    },
    {
      titleText: 'Cuenta Completa',
      dataIndex: 'cuenta_completa',
      key: 'cuenta_completa',
      width: 200,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Cargo/Abono',
      dataIndex: 'es_cargo',
      key: 'es_cargo',
      width: 100,
      render: (val) => (val ? 'Cargo' : 'Abono'),
    },
    {
      titleText: 'Grupo',
      dataIndex: 'grupo',
      key: 'grupo',
      width: 120,
      render: (val) => GRUPO_OPCIONES.find((e) => e.id === val)?.descripcion || 'Todos',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  return (
    <Row className="container" style={{ marginTop: 15 }}>
      <Col span={24}>
        {!configuracionDePolizaCargaExternaId && (
          <Alert
            message="Atención"
            description="Debe guardar el Concepto actual para poder agregar y gestionar renglones de póliza."
            type="warning"
            showIcon
            style={{ marginBottom: 15 }}
          />
        )}
        <Spin tip="Cargando..." spinning={loading}>
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            allowExportPDF={false}
            permission={permission}
            hasAllViewPermission
          />

          <Modal
            open={open}
            closable={false}
            maskClosable={!loading}
            keyboard={!loading}
            footer={null}
            title={(
              <FormSubmitControls
                label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Póliza`}
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                allowSave={!disabled}
              />
            )}
            onCancel={onCancel}
            forceRender
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="poliza_carga_externa_tabla_form"
            >
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="cuenta"
                    label="Cuenta"
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="scta"
                    label="SCTA"
                    rules={[requiredRule]}
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="sscta"
                    label="SSCTA"
                    rules={[requiredRule]}
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="cuenta_completa"
                    label="Cuenta Completa"
                    rules={[requiredRule]}
                    hasFeedback
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Col xs={24} sm={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="es_cargo"
                    label="Cargo / Abono"
                    rules={[requiredRule]}
                    hasFeedback
                  >
                    <Select trueFalse={['Cargo', 'Abono']} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="grupo"
                    label="Grupo"
                    hasFeedback
                    extra="Deja este campo vacío si la cuenta aplica igual para Plantilla, Seguridad y Eventual."
                  >
                    <Select dataSource={GRUPO_OPCIONES} allowClear />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Modal>

          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            open={visibleAlert}
            content="el renglón de póliza seleccionado"
            loading={loading}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default PolizaCargaExternaTabla;
